import { useEffect, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import {
  ClientSessionHashStat,
  fetchClientSessionHashStats,
} from '@/services/clientSessionsApi';

const formatHash = (h: string) =>
  h === 'embedded' ? 'embedded' : h.slice(0, 8);

const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const HashStatsTable = () => {
  const [rows, setRows] = useState<ClientSessionHashStat[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchClientSessionHashStats()
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card>
      <Card.Section>
        <Stack
          gap={8}
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Body weight="semibold">OTA Hash Distribution</Body>
          {rows && (
            <Small color="lighter">
              {rows.length} hash{rows.length === 1 ? '' : 'es'}
            </Small>
          )}
        </Stack>
      </Card.Section>

      {error && (
        <Card.Section>
          <Body color="lighter">Error: {error}</Body>
        </Card.Section>
      )}

      {!error && rows === null && (
        <Card.Section>
          <Stack gap={8} style={{ alignItems: 'center' }}>
            <CircularProgress size={20} />
            <Body color="lighter">Loading…</Body>
          </Stack>
        </Card.Section>
      )}

      {!error && rows && rows.length === 0 && (
        <Card.Section>
          <Body color="lighter">No client sessions recorded yet.</Body>
        </Card.Section>
      )}

      {!error && rows && rows.length > 0 && (
        <Card.Section>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}
          >
            <thead>
              <tr style={{ textAlign: 'left', color: '#6B7280' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  OTA hash
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  Workers
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  Latest request
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ota_hash} style={{ borderTop: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <Tooltip title={r.ota_hash}>
                      <span>
                        <Body>{formatHash(r.ota_hash)}</Body>
                      </span>
                    </Tooltip>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>{r.worker_count}</Body>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Small color="lighter">
                      {formatDate(r.latest_seen_at)}
                    </Small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card.Section>
      )}
    </Card>
  );
};

export default HashStatsTable;
